import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjects } from './ProjectProvider';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Upload, 
  Image as ImageIcon, 
  File, 
  X, 
  Plus, 
  Tag, 
  Clock, 
  Star,
  Lightbulb,
  Palette,
  Download,
  GripVertical,
  CheckCircle,
  FolderOpen,
  AlertCircle
} from 'lucide-react';

const AddProject = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get project ID from URL for edit mode
  const { addProject, updateProject, getProject } = useProjects();
  const mainImageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Determine if we're in edit mode
  const isEditMode = !!id;

  // Form state
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    category: '',
    difficulty: 'Beginner',
    estimatedTime: '',
    tags: [],
    materials: [''],
    mainImageUrl: '',
    mainImageFile: null,
    images: [],
    files: [],
    instructions: '',
    tips: '',
    variations: ''
  });

  const [currentTag, setCurrentTag] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const categories = ['Garments', 'Bags & Accessories', 'Quilting', 'Home Decor', 'Alterations', 'Embroidery', 'Pattern Making', 'Upcycling'];
  const difficulties = ['Beginner', 'Intermediate', 'Advanced'];

  // Load project data for editing
  useEffect(() => {
    if (isEditMode && id) {
      console.log('Loading project for editing:', id);
      const existingProject = getProject(id);
      
      if (existingProject) {
        console.log('Found existing project:', existingProject);
        setProjectData({
          name: existingProject.name || '',
          description: existingProject.description || '',
          category: existingProject.category || '',
          difficulty: existingProject.difficulty || 'Beginner',
          estimatedTime: existingProject.estimatedTime || existingProject.estimated_time || '',
          tags: existingProject.tags || [],
          materials: existingProject.materials && existingProject.materials.length > 0 
            ? existingProject.materials 
            : [''],
          mainImageUrl: existingProject.mainImageUrl || existingProject.main_image_url || '',
          mainImageFile: null,
          images: existingProject.images || existingProject.imageData || [],
          files: existingProject.files || existingProject.fileData || [],
          instructions: existingProject.instructions || '',
          tips: existingProject.tips || '',
          variations: existingProject.variations || ''
        });
      } else {
        console.error('Project not found for editing');
        setSubmitError('Project not found. Redirecting to projects list...');
        setTimeout(() => {
          navigate('/projects');
        }, 2000);
      }
    }
  }, [id, isEditMode, getProject, navigate]);

  const handleInputChange = (field, value) => {
    setProjectData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !projectData.tags.includes(currentTag.trim())) {
      setProjectData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setProjectData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddMaterial = () => {
    setProjectData(prev => ({
      ...prev,
      materials: [...prev.materials, '']
    }));
  };

  const handleMaterialChange = (index, value) => {
    setProjectData(prev => ({
      ...prev,
      materials: prev.materials.map((material, i) => 
        i === index ? value : material
      )
    }));
  };

  const handleRemoveMaterial = (index) => {
    setProjectData(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index)
    }));
  };

  const handleMainImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProjectData(prev => ({
          ...prev,
          mainImageUrl: event.target.result,
          mainImageFile: file
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveMainImage = () => {
    setProjectData(prev => ({
      ...prev,
      mainImageUrl: '',
      mainImageFile: null
    }));
    if (mainImageInputRef.current) {
      mainImageInputRef.current.value = '';
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const newImage = {
            id: Date.now() + Math.random(),
            url: event.target.result,
            name: file.name,
            caption: ''
          };
          setProjectData(prev => ({
            ...prev,
            images: [...prev.images, newImage]
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const newFile = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
        type: getFileType(file.name),
        file: file
      };
      setProjectData(prev => ({
        ...prev,
        files: [...prev.files, newFile]
      }));
    });
  };

  const getFileType = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) return 'Sewing Pattern';
    if (['doc', 'docx'].includes(extension)) return 'Instructions';
    if (['zip', 'rar'].includes(extension)) return 'Pattern Bundle';
    return 'Other';
  };

  const handleImageCaptionChange = (imageId, caption) => {
    setProjectData(prev => ({
      ...prev,
      images: prev.images.map(img => 
        img.id === imageId ? { ...img, caption } : img
      )
    }));
  };

  const handleRemoveImage = (imageId) => {
    setProjectData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  const handleRemoveFile = (fileId) => {
    setProjectData(prev => ({
      ...prev,
      files: prev.files.filter(file => file.id !== fileId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    // Validate required fields
    if (!projectData.name.trim()) {
      setSubmitError('Please enter a project name');
      return;
    }
    if (!projectData.description.trim()) {
      setSubmitError('Please enter a project description');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare data for API - ensure mainImageUrl is included
      const projectDataForAPI = {
        name: projectData.name,
        description: projectData.description,
        category: projectData.category,
        difficulty: projectData.difficulty,
        estimatedTime: projectData.estimatedTime,
        mainImageUrl: projectData.mainImageUrl || '', // Main project photo
        tags: projectData.tags || [],
        materials: projectData.materials.filter(m => m.trim()) || [], // Remove empty materials
        instructions: projectData.instructions,
        tips: projectData.tips,
        variations: projectData.variations,
        images: projectData.images || [],
        files: projectData.files || []
      };

      console.log(`${isEditMode ? 'Updating' : 'Creating'} project:`, projectDataForAPI);
      console.log('Main image URL:', projectDataForAPI.mainImageUrl);
      console.log('Additional images:', projectData.images);
      console.log('Files:', projectData.files);

      // Save or update the project
      let savedProject;
      if (isEditMode) {
        savedProject = await updateProject(id, projectDataForAPI);
        console.log('Project updated successfully:', savedProject);
      } else {
        savedProject = await addProject(projectDataForAPI);
        console.log('Project created successfully:', savedProject);
      }
      
      // Navigate back to projects
      navigate('/projects');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} project:`, error);
      setSubmitError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} project. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/projects')}
              className="p-2 text-gray-400 dark:text-gray-400 hover:text-[#7A99D8] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <FolderOpen className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">
                {isEditMode ? 'Edit Project' : 'Add New Project'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {isEditMode ? 'Update project information' : 'Create a new project for class curriculum'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors font-medium"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button
              form="project-form"
              type="submit"
              disabled={loading}
              className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-gray-100 px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 font-medium"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100"></div>
                  {isEditMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Project' : 'Save Project'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div>{/* Content wrapper */}

        {/* Error Message */}
        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error {isEditMode ? 'updating' : 'creating'} project</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{submitError}</p>
            </div>
            <button
              onClick={() => setSubmitError('')}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <form id="project-form" onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-6">Basic Information</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={projectData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Canvas Tote Bag, Summer Dress, Patchwork Quilt"
                  className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">
                  Description *
                </label>
                <textarea
                  value={projectData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what students will learn and create in this sewing project..."
                  rows={4}
                  className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">Category</label>
                  <select
                    value={projectData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#7A99D8]"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">Difficulty</label>
                  <select
                    value={projectData.difficulty}
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                    className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#7A99D8]"
                  >
                    {difficulties.map(diff => (
                      <option key={diff} value={diff}>{diff}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">Estimated Time</label>
                  <input
                    type="text"
                    value={projectData.estimatedTime}
                    onChange={(e) => handleInputChange('estimatedTime', e.target.value)}
                    placeholder="e.g., 3 hours, 2 sessions, 1 weekend"
                    className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#7A99D8]"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {projectData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#F5F5F5] dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100 text-sm rounded-full"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 text-gray-500 dark:text-gray-300 hover:text-[#3C3C3C] dark:hover:text-gray-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add a tag (e.g., beginner-friendly, handmade, sustainable)..."
                    className="flex-1 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#7A99D8]"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-gray-100 rounded-lg flex items-center gap-1 font-medium"
                  >
                    <Tag className="w-4 h-4" />
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Project Photo */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Main Project Photo</h2>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Upload a main photo that represents this project. This will be shown in project cards and class displays.
              </p>
              
              {!projectData.mainImageUrl ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
                  <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => mainImageInputRef.current?.click()}
                      className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                    >
                      Choose Main Photo
                    </button>
                    <p className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                  <input
                    ref={mainImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="relative">
                  <div className="relative group">
                    <img
                      src={projectData.mainImageUrl}
                      alt="Main project photo"
                      className="w-full h-64 object-cover rounded-lg shadow-sm"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-opacity flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-2">
                        <button
                          type="button"
                          onClick={() => mainImageInputRef.current?.click()}
                          className="bg-white text-gray-700 px-4 py-2 rounded-lg shadow-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveMainImage}
                          className="bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                  <input
                    ref={mainImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="hidden"
                  />
                  <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Main project photo uploaded</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">
                      This image will represent your project in cards and listings
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Project Images */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Additional Project Images</h2>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Upload additional photos to show project details, steps, or variations. These will appear in the project gallery.
              </p>
              
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-center"
                >
                  <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <div className="text-lg font-medium text-gray-700 mb-2">Upload Additional Images</div>
                  <div className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB each</div>
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {projectData.images.length > 0 && (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 mb-4">
                    Drag and drop to reorder additional images
                  </div>
                  {projectData.images.map((image, index) => (
                    <div
                      key={image.id}
                      className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg"
                    >
                      <img
                        src={image.url}
                        alt={`Upload ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-2">{image.name}</div>
                        <input
                          type="text"
                          value={image.caption}
                          onChange={(e) => handleImageCaptionChange(image.id, e.target.value)}
                          placeholder="Add a caption for this image..."
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveImage(image.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Instructions</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Step-by-Step Instructions
                </label>
                <textarea
                  value={projectData.instructions}
                  onChange={(e) => handleInputChange('instructions', e.target.value)}
                  placeholder="Provide detailed step-by-step sewing instructions. Include cutting, pinning, stitching, and finishing details..."
                  rows={8}
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Pro Tips & Advice
                </label>
                <textarea
                  value={projectData.tips}
                  onChange={(e) => handleInputChange('tips', e.target.value)}
                  placeholder="Share helpful sewing tips, common mistakes to avoid, pressing techniques, or professional finishing touches..."
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Variations & Creative Ideas
                </label>
                <textarea
                  value={projectData.variations}
                  onChange={(e) => handleInputChange('variations', e.target.value)}
                  placeholder="Suggest fabric alternatives, size adjustments, embellishment ideas, or style variations..."
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Materials */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-6">Materials & Supplies</h2>
            <div className="space-y-4">
              {projectData.materials.map((material, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={material}
                    onChange={(e) => handleMaterialChange(index, e.target.value)}
                    placeholder="e.g., 2 yards cotton fabric, thread, zipper"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#7A99D8]"
                  />
                  <button
                    onClick={() => handleRemoveMaterial(index)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddMaterial}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-3 text-gray-600 hover:border-[#7A99D8] hover:text-[#7A99D8] hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Material
              </button>
            </div>
          </div>

          {/* Files */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-6">Project Files</h2>
            
            <div className="mb-6">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-[#7A99D8] hover:bg-blue-50 transition-colors text-center"
              >
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <div className="text-sm font-medium text-gray-700 mb-1">Upload Files</div>
                <div className="text-xs text-gray-500">PDF sewing patterns, cutting layouts, size charts</div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            {projectData.files.length > 0 && (
              <div className="space-y-3">
                {projectData.files.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <File className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{file.name}</div>
                      <div className="text-xs text-gray-500">{file.type} • {file.size}</div>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(file.id)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Spacer for sticky buttons */}
          <div className="h-20"></div>
        </form>
        </div>

        {/* Sticky Form Actions */}
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="px-6 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="project-form"
              disabled={loading}
              className="px-6 py-2 bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Project' : 'Create Project'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProject;